import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from 'src/app/services/services/settings.service';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { Profile, realtimeUsersInGroup } from 'src/app/app-models/realTimeModels';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { IHelpDialogData } from 'src/app/models';
import { ProfileDialogComponent } from '../../real-time-dialogs/profile-dialog/profile-dialog.component';
import { SetPauseDialogComponent } from '../../real-time-dialogs/set-pause-dialog/set-pause-dialog.component';
import { NGXLogger } from 'ngx-logger';
import { ErrorMessage, InfoMessage } from 'src/app/common/messages';
import * as mainActions from 'src/app/state/app.actions';
import { Store } from '@ngrx/store';
import { State } from 'src/app/dashboards/state';
import { SetLogOnDialogComponent } from '../../real-time-dialogs/set-logOn-dialog/set-logOn-dialog.component';



@Component({
    selector: 'app-user-groups-getgroup',
    templateUrl: './user-groups-getgroup.component.html',
    styleUrls: ['./user-groups-getgroup.component.scss'],
    standalone: false
})
export class UserGroupsGetgroupComponent implements OnInit {
  @ViewChild(MatSort) sort: MatSort;

  isLoading = true;
  filter: string = '';
  allAgents: boolean = false;
  refreshInterval: number = 10000; // default to 10s
  dataSource = new MatTableDataSource<realtimeUsersInGroup>([]);
  displayedColumns: string[] = ["formattedName", "phoneNumber", "profileName", "formattedStatus", "secondsInStatus"];
  BreadcrumbsLink = [];

  private refreshTimer: any;

  groupName: string = '';
  type: string = 'user-groups';

  constructor(private realTimeService: realTimeService, private logger: NGXLogger, private store: Store<State>, private settingsService: SettingsService, private dialog: MatDialog, private i18nextPipe: I18NextPipe, private route: ActivatedRoute, private router: Router) { }

  async ngOnInit(): Promise<void> {
    let that = this;
    // Get the group name from route parameters
    this.route.params.subscribe(params => {
      that.groupName =this.decodeURIComponent(params['name'] || '');
      that.allAgents = !that.groupName;
      this.BreadcrumbsLink = [
        { "label": "Home", "url": "/" },
        { "label": "Real-Time", "url": "/real-time/overview" },
        { "label": "User Groups", "url": "/real-time/user-groups" },
        { "label": that.allAgents ? "All Agents" : this.decodeURIComponent(that.groupName), "url": that.allAgents ? "/real-time/user-groups/all-agents" : `/real-time/user-groups/get-group/${that.groupName}` }];

      that.getData();
    });

    var settings = await this.settingsService.realtimeSettings();
    if (settings?.canChangeAgentStatus == true) {
      this.displayedColumns.push("changeStatus");
    }

    this.dataSource.sortingDataAccessor = (row: realtimeUsersInGroup, column: string) => {
      switch (column) {
        case 'formattedName': return (row.formattedName || '').toLowerCase();
        case 'phoneNumber': return (row.formattedPhoneNumber || row.phoneNumber || '').toLowerCase();
        case 'profileName': return (row.profileName || '').toLowerCase();
        case 'formattedStatus': return this.statusOrderFromId(row.simpleStatus);
        case 'secondsInStatus': return row.secondsInStatus;
        default: return '';
      }
    };


    this.refreshInterval = Number(settings?.refreshInterval) * 1000 || 10000;
    this.startRefreshTimer();
  }

  ngAfterViewInit(): void {
    // Connect the sort to the data source after the view is initialized
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filter = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  applyFilterFromProperty() {
    this.dataSource.filter = this.filter.trim().toLowerCase();
  }

  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  getData(): void {
    this.realTimeService.GetUsersInGroup(encodeURIComponent(this.groupName)).subscribe({
      next: (response) => {
        this.dataSource.data = response;
        // Ensure sort is connected after data loads
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
        // Reapply filter if one exists
        if (this.filter) {
          this.applyFilterFromProperty();
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.logger.error('Error fetching user groups:', error);
        this.isLoading = false;
      }
    });

  }

  trackById(index: number, item: realtimeUsersInGroup): number {
    return item.userId;
  }


  openHelp(): void {
    let data: IHelpDialogData = { title: this.i18nextPipe.transform('realtime:help_title_usersInGroup'), text: this.i18nextPipe.transform('realtime:help_usersInGroup') };
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: "backdrop-invisible",
      data,
      width: "600px"
    });
  }

  openChangeStatusDialog(element: realtimeUsersInGroup, status: string): void {
    this.logger.debug(`Change status for user ${element.userId} to ${status}`);

    if (status === 'setPause' || status === 'changePause') {
      this.openSetPauseDialog(element);
    } else if (status === 'logOn') {
      this.openSetLogOnDialog(element);
    }
    else if (status === 'logOff') {
      this.realTimeService.SetUserLogOff(element.userId).subscribe({
        next: () => {
          this.logger.debug(`Agent set log off for user ${element.userId}`);
          this.addInfoMessage(`Agent status updated successfully`);
          this.getData();
        },
        error: (error) => {
          this.logger.error(`Error setting agent log off for user ${element.userId}: ${error}`);
          this.addErrorMessage(`Agent status updated failed`);
        }
      });
    }
    else if (status === 'setReady') {
      this.realTimeService.SetUserAvailable(element.userId).subscribe({
        next: () => {
          this.logger.debug(`Agent set ready for user ${element.userId}`);
          this.addInfoMessage(`Agent status updated successfully`);
          this.getData();
        },
        error: (error) => {
          this.logger.error(`Error setting agent ready for user ${element.userId}: ${error}`);
          this.addErrorMessage(`Agent status updated failed`);
        }
      });
    }
  }

  openSetPauseDialog(element: realtimeUsersInGroup): void {
    let dialogRef = this.dialog.open(SetPauseDialogComponent, {
      width: '600px',
      data: { pauseDescription: element.pauseDescription }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) { return; }
      this.logger.debug(`Result from set pause dialog: ${result}`);
      this.realTimeService.SetAgentPause(element.userId, result).subscribe({
        next: () => {
          this.logger.debug(`Agent pause set for user ${element.userId}`);
          this.addInfoMessage(`Agent status updated successfully`);
          this.getData();
        },
        error: (error) => {
          this.logger.error(`Error setting agent pause for user ${element.userId}: ${error}`);
          this.addErrorMessage(`Agent status updated failed`);
        }
      });
    });
  }

  openSetLogOnDialog(element: realtimeUsersInGroup): void {
    let dialogRef = this.dialog.open(SetLogOnDialogComponent, {
      data: { userId: element.userId, phoneNumber: element.phoneNumber },
      width: '600px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) { return; }
      this.logger.debug(`Result from set log on dialog: ${result}`);
      this.realTimeService.SetUserLogOn(result.phoneNumber, element.userId, result.profileId, result.profileNumber).subscribe({
        next: () => {
          this.logger.debug(`Agent log on set for user ${element.userId}`);
          this.addInfoMessage(`Agent status updated successfully`);
          this.getData();
        },
        error: (error) => {
          this.logger.error(`Error setting agent log on for user ${element.userId}: ${error}`);
          this.addErrorMessage(`Agent status updated failed`);
        }
      });
    });
  }

  openProfile(element: realtimeUsersInGroup): void {
    this.logger.debug(`Change profile for user ${element.userId}`);
    this.dialog.open(ProfileDialogComponent, {
      data: { userId: element.userId },
      width: '600px'
    });

  }

  private startRefreshTimer(): void {
    // Clear any existing timer
    this.stopRefreshTimer();

    // Start new timer
    this.refreshTimer = setInterval(() => {
      this.getData();
    }, this.refreshInterval);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private addErrorMessage(message: string): void {
    this.store.dispatch(
      mainActions.addMessage({
        message: new ErrorMessage(this.i18nextPipe.transform(`messages:${message}`), '')
      }));
  }

  private addInfoMessage(message: string): void {
    this.store.dispatch(
      mainActions.addMessage({
        message: new InfoMessage(this.i18nextPipe.transform(`messages:${message}`), '')
      }));
  }

  decodeURIComponent(value: string): string {
    return decodeURIComponent(value);
  }

  private statusOrderFromId(id?: number): number {
    switch (id) {
      case 2: return 10; // Available
      case 5: return 20; // Connected
      case 3: return 30; // Allocated
      case 9: return 40; // Busy
      case 8: return 60; // Pause
      case 1: return 70; // LoggedOff
      default: return 50;
    }
  }

  ngOnDestroy(): void {
    this.stopRefreshTimer();
  }
}
